import type {
  FiscalData,
  FiscalIssuer,
  FiscalTransparency,
} from "@/types/printer";

/**
 * Helpers para mapear el comprobante fiscal AFIP (emitido en `createOrderV2`
 * con `emitInvoice: true`, o persistido en `order.invoiceHistory[]`) a los
 * `FiscalData` que consumen `generateTicketHTML` / `renderTicketXml`.
 *
 * Contrato backend (trd-core #883/#884): `cbteTipo` 1=A, 6=B, 11=C; `qrData`
 * ya viene como URL completa y escaneable de AFIP (no anteponer prefijo).
 */

/**
 * Normaliza una fecha del comprobante a `dd/MM/yyyy` para imprimirla en el
 * ticket: el vencimiento del CAE y el inicio de actividades del emisor. AFIP la
 * emite como `YYYYMMDD`; trd-core puede serializarla como ISO. Es una
 * transformación puramente de string (sin `new Date`) para no arrastrar bugs de
 * timezone al comprobante. Si no matchea ningún formato conocido, se deja igual.
 */
export function formatFiscalDate(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim();
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (compact) return `${compact[3]}/${compact[2]}/${compact[1]}`;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
}

/** Código de tipo de comprobante AFIP (`cbteTipo`) → etiqueta A/B/C. */
export function invoiceTypeLabel(cbteTipo: unknown): string {
  switch (Number(cbteTipo)) {
    case 1:
      return "A";
    case 6:
      return "B";
    case 11:
      return "C";
    default:
      return "";
  }
}

/**
 * `30712345678` → `30-71234567-8`, como se imprime en el ticket. Si no son 11
 * dígitos se deja como vino: un CUIT mal cargado se ve tal cual, no rearmado.
 */
export function formatCuit(raw?: string | null): string | undefined {
  const s = texto(raw);
  if (!s) return undefined;
  const digits = s.replace(/\D/g, "");
  if (digits.length !== 11) return s;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

/** El emisor como lo manda core en `IssueInvoiceResult.issuer` (contrato §2.4). */
export interface InvoiceIssuerResult {
  cuit?: string | null;
  legalName?: string | null;
  ivaCondition?: string | null;
  grossIncomeNumber?: string | null;
  /** `yyyy-mm-dd`. */
  activityStartDate?: string | null;
  address?: string | null;
}

/** `IssueInvoiceResult.transparency`: montos con dos decimales y punto. */
export interface InvoiceTransparencyResult {
  ivaContenido?: string | null;
  otrosImpuestosNacionalesIndirectos?: string | null;
}

/** Forma del `invoice` que devuelve `POST /order/orders/v2` (response.data.data.invoice). */
export interface InvoiceEmissionResult {
  invoiceId?: string;
  status?: "drafting" | "authorized" | "pending_retry" | "failed" | "voided";
  cae?: string | null;
  caeDueDate?: string | null;
  voucherNumber?: string | null;
  cbteTipo?: number | null;
  qrData?: string | null;
  invoicePath?: string | null;
  /** Desde trd-core#1182 (v1.77.0). */
  issuer?: InvoiceIssuerResult | null;
  transparency?: InvoiceTransparencyResult | null;
}

/** Un string con contenido, o nada: un renglón vacío no se imprime. */
function texto(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const s = value.trim();
  return s === "" ? undefined : s;
}

/**
 * Un monto del bloque de transparencia, como string decimal con punto. Core
 * los manda como string; un número se acepta igual, sin pasar por el formato
 * argentino de `convertToNumberValue`, que lee el punto como separador de miles.
 */
function monto(value: unknown): string | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(2) : undefined;
  }
  return texto(value);
}

/** El emisor de core → lo que imprime el ticket. Sin ningún dato, no hay bloque. */
export function fiscalIssuerFrom(
  raw?: InvoiceIssuerResult | null
): FiscalIssuer | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const issuer: FiscalIssuer = {
    cuit: formatCuit(raw.cuit),
    legalName: texto(raw.legalName),
    ivaCondition: texto(raw.ivaCondition),
    grossIncomeNumber: texto(raw.grossIncomeNumber),
    activityStartDate: formatFiscalDate(texto(raw.activityStartDate)),
    address: texto(raw.address),
  };
  return Object.values(issuer).some(Boolean) ? issuer : undefined;
}

/**
 * El bloque de transparencia de core → el del ticket. Core decide cuándo va
 * (sólo B o C a consumidor final, y nunca en una nota de crédito o débito): el
 * cliente lo imprime si viene, sin repetir la regla. Si falta uno de los dos
 * montos no se imprime el bloque: no se inventa un cero.
 */
export function fiscalTransparencyFrom(
  raw?: InvoiceTransparencyResult | null
): FiscalTransparency | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const ivaContenido = monto(raw.ivaContenido);
  const otros = monto(raw.otrosImpuestosNacionalesIndirectos);
  if (ivaContenido === undefined || otros === undefined) return undefined;
  return { ivaContenido, otrosImpuestosNacionalesIndirectos: otros };
}

/**
 * Mapea el `invoice` del response de createOrderV2 → `FiscalData`.
 * Devuelve `undefined` (⇒ ticket NO fiscal) salvo que el comprobante esté
 * `authorized` y tenga CAE — así la venta nunca se bloquea por la factura.
 */
export function fiscalDataFromInvoice(
  invoice?: InvoiceEmissionResult | null
): FiscalData | undefined {
  if (!invoice || invoice.status !== "authorized" || !invoice.cae) {
    return undefined;
  }
  return {
    cae: invoice.cae,
    caeExpiration: formatFiscalDate(invoice.caeDueDate),
    invoiceNumber: invoice.voucherNumber ?? undefined,
    invoiceType: invoiceTypeLabel(invoice.cbteTipo),
    qrData: invoice.qrData ?? undefined,
    issuer: fiscalIssuerFrom(invoice.issuer),
    transparency: fiscalTransparencyFrom(invoice.transparency),
  };
}

/**
 * Mapea la última entrada facturada de `order.invoiceHistory[]` → `FiscalData`
 * (reimpresión de tickets ya emitidos). Post trd-core#883 la entrada trae
 * `cae` / `caeDueDate` / `voucherNumber` / `cbteTipo` / `qrData`. El emisor y la
 * transparencia se leen si vienen, con la forma de la emisión: es la ruta que se
 * le propuso a core en trd-core#1191. Sin ellos la reimpresión sale sin esos
 * dos bloques; el emisor no se arma con los datos de hoy del negocio, porque el
 * ticket es la foto del comprobante.
 */
export function fiscalDataFromOrderHistory(order: any): FiscalData | undefined {
  const history: any[] = Array.isArray(order?.invoiceHistory)
    ? order.invoiceHistory
    : [];
  // Última entrada facturada: recorre de atrás hacia adelante y corta al primer
  // match, sin copiar/invertir el array (js-early-exit).
  let entry: any;
  for (let i = history.length - 1; i >= 0; i--) {
    const e = history[i];
    if (e?.cae && (e?.invoicePath || e?.invoicedAt)) {
      entry = e;
      break;
    }
  }
  if (!entry?.cae) return undefined;

  return {
    cae: entry.cae,
    caeExpiration: formatFiscalDate(entry.caeDueDate),
    invoiceNumber: entry.voucherNumber ?? undefined,
    // `cbteTipo` del comprobante (autoritativo); fallback al tipo del comprador.
    invoiceType: invoiceTypeLabel(
      entry.cbteTipo ?? order?.buyerTaxInfo?.invoiceType
    ),
    qrData: entry.qrData ?? undefined,
    issuer: fiscalIssuerFrom(entry.issuer),
    transparency: fiscalTransparencyFrom(entry.transparency),
  };
}
